import { useSearchParams } from "react-router-dom";
import { Receipt, AlertCircle, FileText, LifeBuoy, FolderKanban, ExternalLink, Wallet, Sparkles } from "lucide-react";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@wa/ui";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { ADMIN_URL, withLang, type AccountUser, type Membership } from "../../lib/auth";
import { type PortalSummary } from "../../lib/portal";
import type { PortalModule } from "@wa/core/portal-modules";
import { type AccountCredit } from "../../lib/credit";
import { isClientPortalHost } from "../../lib/brand";
import { fmtDate, fmtMoney } from "../../lib/format";
import { keepPreview } from "../../lib/previewMode";

/** Does the membership grant site management (the same logic as the header's "Open editor")? */
const canManage = (m: Membership) =>
  m.isSuperadmin || m.permissions.includes("*") || m.permissions.some((p) => p.startsWith("manage:"));

export function OverviewSection({
  user, summary, credit, referralEnabled, onInvite,
}: {
  user: AccountUser;
  summary: PortalSummary | null;
  /** Account wallet; `null` = the module is off or the user has no account. */
  credit: AccountCredit | null;
  /** Is there a way to earn more credit right now (the referral programme)? */
  referralEnabled: boolean;
  onInvite: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [, setParams] = useSearchParams();

  const stats = summary?.linked ? summary.stats : null;
  // Which of the four tiles this contact is entitled to. A tile whose module is
  // closed would print a number the client cannot open a list to explain - and
  // for invoices it would print a SUM, which is the one number on this page
  // somebody might act on.
  const shows = (m: PortalModule) => !!summary?.modules?.includes(m);
  // Invoices draws two tiles of its own, so the count is not the module count.
  const tileCount =
    (shows("invoices") ? 2 : 0) +
    (shows("estimates") ? 1 : 0) +
    (shows("projects") ? 1 : 0) +
    (shows("support") ? 1 : 0);
  // Written out rather than composed: Tailwind reads class names as literals,
  // and a `lg:grid-cols-${n}` would be swept out of the build with nothing to
  // show for it but a row of full-width cards.
  const TILE_GRID: Record<number, string> = {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-2 lg:grid-cols-4",
    5: "grid-cols-2 lg:grid-cols-5",
  };

  // The wallet card is shown whenever the module is on - including at zero, so the
  // client can see the wallet exists and how to fill it (that is the whole point of
  // a signup/referral credit they have not collected yet).
  const hasBalance = !!credit && (credit.availableCents > 0 || credit.pendingCents > 0);

  // The client portal draws neither the Husly sites card nor the wallet, so a
  // contact with no open modules is left with a blank page - and a blank page
  // reads as broken rather than as empty. Say what would fill it instead. On
  // Husly's own hub the sites card always renders, so this never fires there.
  const nothingToShow = isClientPortalHost() && !credit && !(stats && tileCount > 0);

  return (
    <div className="space-y-6">
      {/* ── Portal summary (a linked client only, and only their modules) ── */}
      {stats && tileCount > 0 && (
        <div className={`grid gap-3 ${TILE_GRID[tileCount] ?? "grid-cols-2 lg:grid-cols-4"}`}>
          {shows("invoices") && (
            <StatCard
              icon={Receipt}
              label={t("portal.overview.outstanding")}
              value={fmtMoney(stats.outstandingCents, "EUR", locale)}
              tone={stats.outstandingCents > 0 ? "warning" : "default"}
              onClick={() => setParams(keepPreview({ tab: "invoices" }))}
            />
          )}
          {shows("invoices") && (
            <StatCard
              icon={AlertCircle}
              label={t("portal.overview.overdue")}
              value={String(stats.overdueInvoices)}
              tone={stats.overdueInvoices > 0 ? "destructive" : "default"}
              onClick={() => setParams(keepPreview({ tab: "invoices" }))}
            />
          )}
          {shows("estimates") && (
            <StatCard
              icon={FileText}
              label={t("portal.overview.pendingEstimates")}
              // The only tile on this page that is a TASK for the reader rather
              // than a fact about them, so it is toned whenever it is not zero.
              value={String(stats.pendingEstimates)}
              tone={stats.pendingEstimates > 0 ? "warning" : "default"}
              onClick={() => setParams(keepPreview({ tab: "estimates" }))}
            />
          )}
          {shows("projects") && (
            <StatCard
              icon={FolderKanban}
              label={t("portal.overview.activeProjects")}
              value={String(stats.activeProjects)}
              onClick={() => setParams(keepPreview({ tab: "projects" }))}
            />
          )}
          {shows("support") && (
            <StatCard
              icon={LifeBuoy}
              label={t("portal.overview.openTickets")}
              value={String(stats.openTickets)}
              onClick={() => setParams(keepPreview({ tab: "support" }))}
            />
          )}
        </div>
      )}

      {/* ── Account credit (the wallet) ──────────────────────────────────── */}
      {credit && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="size-5 text-primary" />
              {t("credit.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("credit.available")}</div>
                <div className="text-3xl font-bold tabular-nums">{fmtMoney(credit.availableCents, "EUR", locale)}</div>
              </div>
              {credit.pendingCents > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("credit.pending")}</div>
                  <div className="text-lg font-medium tabular-nums text-muted-foreground">
                    {fmtMoney(credit.pendingCents, "EUR", locale)}
                  </div>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {hasBalance ? t("credit.spendHint") : t("credit.zeroHint")}
            </p>
            {credit.nextExpiryAt && (
              <p className="text-xs text-muted-foreground">
                {t("credit.expiresAt", { date: fmtDate(credit.nextExpiryAt, locale) })}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {referralEnabled && (
                <Button size="sm" onClick={onInvite}>
                  <Sparkles className="size-3.5" />
                  {t("credit.getFree")}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setParams(keepPreview({ tab: "billing" }))}>
                {t("credit.viewLedger")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Husly sites. Hidden on the operator's client portal: that client has no
          Husly site, and "Create your first site" is an invitation into a product
          they did not come here for. See lib/brand.ts. */}
      {!isClientPortalHost() && (
      <Card>
        <CardHeader>
          <CardTitle>{t("overview.sites")}</CardTitle>
        </CardHeader>
        <CardContent>
          {user.memberships.length === 0 ? (
            // A user without a site (e.g. a Google signup is account-first) - give a
            // direct path into creating the first site (admin "New site", SSO with the
            // same cookie), so the account hub is not a dead end.
            <div className="flex flex-col items-start gap-3">
              <p className="text-sm text-muted-foreground">{t("overview.noSites")}</p>
              <Button asChild>
                <a href={withLang(`${ADMIN_URL}/sites/new`)}>
                  <ExternalLink className="size-3.5" />
                  {t("overview.createFirstSite")}
                </a>
              </Button>
            </div>
          ) : (
            <ul className="space-y-2">
              {user.memberships.map((m) => (
                <li key={m.siteId} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
                  <span className="min-w-0 truncate text-sm font-medium">{m.siteName}</span>
                  <div className="flex items-center gap-1.5">
                    {m.isHost && <Badge variant="secondary">host</Badge>}
                    <Badge variant="outline">{m.roleName?.[locale] || m.roleName?.en || m.roleName?.et || m.roleKey}</Badge>
                    {canManage(m) && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={withLang(`${ADMIN_URL}/?site=${m.siteId}`)}>
                          <ExternalLink className="size-3.5" />
                          {t(m.isHost ? "app.openCms" : "app.openEditor")}
                        </a>
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      )}

      {nothingToShow && (
        <Card>
          <CardHeader>
            <CardTitle>{t("portal.overview.emptyTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {t("portal.overview.emptyBody")}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({
  icon: Icon, label, value, tone = "default", onClick,
}: {
  icon: typeof Receipt;
  label: string;
  value: string;
  tone?: "default" | "warning" | "destructive";
  onClick: () => void;
}) {
  const toneCls =
    tone === "destructive" ? "text-destructive" : tone === "warning" ? "text-warning" : "text-foreground";
  return (
    <button
      onClick={onClick}
      className="rounded-xl border border-border bg-card/70 p-4 text-left shadow-soft backdrop-blur transition-colors hover:border-primary/40 hover:bg-accent/30"
    >
      <Icon className={`size-5 ${tone === "default" ? "text-muted-foreground" : toneCls}`} />
      <div className={`mt-2 text-2xl font-bold tabular-nums ${toneCls}`}>{value}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
    </button>
  );
}
